import React, { useState } from 'react';
import { 
  CheckCircle2, XCircle, ExternalLink, ChevronDown, ChevronUp,
  Globe, Image, FileText, AlertTriangle, Sparkles, ArrowRight, ArrowDown, X,
  Send, MessageSquare, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Example URLs and content
const EXAMPLE_URL = "https://www.blueridgeparkway.org/communities/sparta-alleghany-county/";
const EXAMPLE_EMAIL_PREVIEW = "https://customer-assets.emergentagent.com/job_7a1f6cd5-42c6-490a-a613-e41c55c1c770/artifacts/m9kpo3ib_email%20preview.png";
const EXAMPLE_WEBPAGE_SCREENSHOT = "https://customer-assets.emergentagent.com/job_7a1f6cd5-42c6-490a-a613-e41c55c1c770/artifacts/nfwa6jbw_scraped%20URL%20example.png";
const EXAMPLE_SOCIAL_POST = "https://customer-assets.emergentagent.com/job_7a1f6cd5-42c6-490a-a613-e41c55c1c770/artifacts/bhp18m41_Mock%20social.png";

export function GettingStartedChecklist({ onDismiss, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const worksWell = [
    "Blog posts with multiple paragraphs",
    "Blog with multiple embedded pictures",
    "Website with a clear, visible logo"
  ];

  const doesntWorkWell = [
    "Pages behind logins or paywalls",
    "Heavy JavaScript/React single-page apps",
    "PDF documents or downloadable files",
    "Pages with mostly video content",
    "Social media posts (Facebook, Instagram)",
    "Very short pages with little text"
  ];

  // Collapsed state - just show the headline
  if (!expanded) {
    return (
      <div className="bg-afa-navy-light rounded-xl p-4 mb-6 border border-afa-navy/10">
        <button
          onClick={() => setExpanded(true)}
          className="w-full flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-lime-500 rounded-full flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <h3 className="font-semibold text-afa-navy">How Simply Smarter Email Works: Start Here</h3>
          </div>
          <ChevronDown className="w-5 h-5 text-slate-400" />
        </button>
      </div>
    );
  }

  // Expanded state - full content
  return (
    <div className="bg-afa-navy-light rounded-xl p-6 mb-6 border border-afa-navy/10">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-lime-500 rounded-full flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-afa-navy">How Simply Smarter Email Works: Start Here</h3>
          </div>
        </div>
        <button onClick={() => setExpanded(false)} className="text-slate-400 hover:text-slate-600">
          <ChevronUp className="w-5 h-5" />
        </button>
      </div>

      {/* Summary Introduction */}
      <div className="mb-6 p-4 bg-white rounded-xl border border-slate-200">
        <p className="text-sm text-slate-600">
          Give us any webpage URL and we'll automatically create a beautiful email newsletter from its content. 
          You customize the design, then send it to your list. We'll even generate social media posts for you!
        </p>
      </div>

      {/* Here's how it works label */}
      <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Here's an example of that:</p>

      {/* Visual 3-Step Process - Linear Flow with Clear Ownership */}
      <div className="mb-6">
        <div className="grid md:grid-cols-3 gap-4">
          {/* Step 1: YOU provide URL */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="w-8 h-8 bg-lime-500 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-bold">1</span>
              </div>
              <span className="text-xs font-bold text-lime-600 uppercase tracking-wide">You</span>
            </div>
            <p className="text-sm font-semibold text-slate-800 mb-1">Paste a URL</p>
            <p className="text-xs text-slate-500 mb-2">That's all you need!</p>
            <div className="bg-white rounded-xl border border-slate-200 p-2 shadow-sm overflow-hidden">
              <img 
                src={EXAMPLE_WEBPAGE_SCREENSHOT}
                alt="Example webpage - Blue Ridge Parkway"
                className="w-full h-48 object-cover object-top rounded-lg"
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">
              We used <a href="https://www.blueridgeparkway.org/communities/sparta-alleghany-county/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-teal-600 underline hover:text-teal-800">blueridgeparkway.org/sparta</a> for this example
            </p>
          </div>
          
          {/* Step 2: WE create email */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="w-8 h-8 bg-lime-500 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-bold">2</span>
              </div>
              <span className="text-xs font-bold text-lime-600 uppercase tracking-wide">We</span>
            </div>
            <p className="text-sm font-semibold text-slate-800 mb-1">Create your email</p>
            <p className="text-xs text-slate-500 mb-3">You pick colors & design</p>
            <div className="bg-white rounded-xl border border-slate-200 p-2 shadow-sm overflow-hidden">
              <img 
                src={EXAMPLE_EMAIL_PREVIEW}
                alt="Generated email"
                className="w-full h-48 object-cover object-top rounded-lg"
              />
            </div>
          </div>
          
          {/* Step 3: BONUS social posts */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="w-8 h-8 bg-lime-500 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-bold">3</span>
              </div>
              <span className="text-xs font-bold text-lime-600 uppercase tracking-wide">Bonus</span>
            </div>
            <p className="text-sm font-semibold text-slate-800 mb-1">Social posts generated</p>
            <p className="text-xs text-slate-500 mb-3">Ready to share!</p>
            <div className="bg-white rounded-xl border border-slate-200 p-2 shadow-sm overflow-hidden">
              <img 
                src={EXAMPLE_SOCIAL_POST}
                alt="Generated social media post"
                className="w-full h-48 object-cover object-top rounded-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {/* What makes a good page - compact visual checklist */}
      <div className="mb-6 p-4 bg-white rounded-xl border border-slate-200">
        <h4 className="text-sm font-bold text-slate-700 mb-3">What makes a page work well?</h4>
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-3 bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg border border-teal-200">
            <Globe className="w-6 h-6 text-teal-600 mx-auto mb-1" />
            <p className="text-xs font-medium text-teal-700">Public URL</p>
            <p className="text-xs text-slate-500">No login needed</p>
          </div>
          <div className="text-center p-3 bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg border border-teal-200">
            <FileText className="w-6 h-6 text-teal-600 mx-auto mb-1" />
            <p className="text-xs font-medium text-teal-700">Text Content</p>
            <p className="text-xs text-slate-500">Multiple paragraphs</p>
          </div>
          <div className="text-center p-3 bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg border border-teal-200">
            <Image className="w-6 h-6 text-teal-600 mx-auto mb-1" />
            <p className="text-xs font-medium text-teal-700">Images</p>
            <p className="text-xs text-slate-500">Photos & graphics</p>
          </div>
        </div>
      </div>

      {/* Two columns: For Best Results / Not designed for */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="p-4 bg-gradient-to-br from-lime-50 to-emerald-50 rounded-xl border border-lime-200">
          <h4 className="text-sm font-bold text-lime-800 mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            For Best Results:
          </h4>
          <ul className="space-y-2">
            {worksWell.map((item, idx) => (
              <li key={idx} className="text-sm text-lime-900 flex items-start gap-2">
                <span className="text-lime-500 mt-0.5">✓</span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200">
          <h4 className="text-sm font-bold text-slate-600 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Not designed for:
          </h4>
          <ul className="space-y-2">
            {doesntWorkWell.map((item, idx) => (
              <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                <span className="text-slate-400 mt-0.5">✗</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Pro tip */}
      <div className="p-4 bg-white rounded-xl border border-slate-200">
        <p className="text-sm text-slate-600 leading-relaxed">
          <span className="font-semibold text-afa-navy">Pro tip:</span> We're looking for a{' '}
          <span className="font-semibold">basic blog post or webpage with pictures</span>.
          The more text and images on the page, the better your email will turn out!
        </p>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
          Here's another example of a website that would be perfect for this tool:{' '}
          <a
            href="https://www.jasperhillfarm.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#0d9488] font-semibold hover:underline"
            data-testid="protip-example-link"
          >
            jasperhillfarm.com →
          </a>
        </p>
      </div>
    </div>
  );
}

// Troubleshooting Help component for the preview page
export function TroubleshootingHelp({ isOpen, onClose }) {
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackType, setFeedbackType] = useState('issue');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackEmail, setFeedbackEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const worksWell = [
    "Blog posts with multiple paragraphs",
    "Blog with multiple embedded pictures",
    "Website with a clear, visible logo"
  ];

  const doesntWorkWell = [
    "Pages behind logins or paywalls",
    "Heavy JavaScript/React single-page apps",
    "PDF documents or downloadable files",
    "Pages with mostly video content",
    "Social media posts (Facebook, Instagram)",
    "Very short pages with little text"
  ];

  const handleSubmitFeedback = async () => {
    if (!feedbackMessage.trim()) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, {
        type: feedbackType,
        message: feedbackMessage,
        email: feedbackEmail || 'anonymous',
        timestamp: new Date().toISOString()
      });
      setSubmitted(true);
      setFeedbackMessage('');
      setFeedbackEmail('');
      setTimeout(() => {
        setSubmitted(false);
        setShowFeedbackForm(false);
      }, 2000);
    } catch (err) {
      console.error('Feedback submission failed:', err);
      // Still show success to user - we'll store locally as backup
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setShowFeedbackForm(false);
      }, 2000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setShowFeedbackForm(false);
    setSubmitted(false);
    setFeedbackMessage('');
    setFeedbackEmail('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-afa-navy rounded-full flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-afa-navy">Having Trouble?</h3>
          </div>
          <button onClick={handleClose} className="text-slate-400 hover:text-afa-navy">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6">
          {/* Intro reminder */}
          <div className="mb-4 p-4 bg-afa-navy rounded-xl">
            <p className="text-sm text-white">
              <span className="font-semibold">Remember:</span> Simply Smarter Email creates newsletters by reading content from a webpage URL. 
              <span className="font-semibold"> A clean, content-rich URL is the key to great results!</span>
            </p>
          </div>

          {/* What makes a good page */}
          <div className="mb-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
            <h4 className="text-sm font-bold text-afa-navy mb-3">What makes a page work well?</h4>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="text-center p-3 bg-white rounded-lg border border-slate-200 shadow-sm">
                <Globe className="w-6 h-6 text-afa-teal mx-auto mb-1" />
                <p className="text-xs font-medium text-afa-navy">Public URL</p>
                <p className="text-xs text-slate-500">No login needed</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg border border-slate-200 shadow-sm">
                <FileText className="w-6 h-6 text-afa-teal mx-auto mb-1" />
                <p className="text-xs font-medium text-afa-navy">Text Content</p>
                <p className="text-xs text-slate-500">Multiple paragraphs</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg border border-slate-200 shadow-sm">
                <Image className="w-6 h-6 text-afa-teal mx-auto mb-1" />
                <p className="text-xs font-medium text-afa-navy">Images</p>
                <p className="text-xs text-slate-500">Photos & graphics</p>
              </div>
            </div>
            <p className="text-sm text-slate-600">
              <span className="font-semibold text-afa-navy">Pro tip:</span> We're looking for a{' '}
              <span className="font-semibold">basic blog post or webpage with pictures</span>. 
              The more text and images on the page, the better your email will turn out!
            </p>
          </div>

          {/* Two columns: For Best Results / Not designed for */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="p-4 bg-white rounded-xl border-2 border-afa-lime">
              <h4 className="text-sm font-bold text-afa-navy mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-afa-teal" />
                For Best Results:
              </h4>
              <ul className="space-y-2">
                {worksWell.map((item, idx) => (
                  <li key={idx} className="text-sm text-slate-700 flex items-start gap-2">
                    <span className="text-afa-teal mt-0.5">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
              <h4 className="text-sm font-bold text-slate-600 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Not designed for:
              </h4>
              <ul className="space-y-2">
                {doesntWorkWell.map((item, idx) => (
                  <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                    <span className="text-slate-400 mt-0.5">✗</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Let AI do the heavy lifting */}
          <div className="mb-6 p-5 bg-afa-navy rounded-xl text-center">
            <h4 className="text-base font-bold text-white mb-2">Let AI do the heavy lifting:</h4>
            <p className="text-sm text-white/80 mb-5">AI brings in your content and images, then you pick the images, headlines, layouts and colors.</p>
            <div className="grid grid-cols-2 gap-3 mb-5">
              <div className="bg-white/10 rounded-lg p-3 flex items-center gap-2">
                <div className="w-8 h-8 bg-afa-lime rounded-full flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-afa-navy" />
                </div>
                <span className="text-sm text-white font-medium">Choose your colors</span>
              </div>
              <div className="bg-white/10 rounded-lg p-3 flex items-center gap-2">
                <div className="w-8 h-8 bg-afa-lime rounded-full flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-afa-navy" />
                </div>
                <span className="text-sm text-white font-medium">Choose your layout</span>
              </div>
              <div className="bg-white/10 rounded-lg p-3 flex items-center gap-2">
                <div className="w-8 h-8 bg-afa-lime rounded-full flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-afa-navy" />
                </div>
                <span className="text-sm text-white font-medium">Let AI help with headlines</span>
              </div>
              <div className="bg-white/10 rounded-lg p-3 flex items-center gap-2">
                <div className="w-8 h-8 bg-afa-lime rounded-full flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-afa-navy" />
                </div>
                <span className="text-sm text-white font-medium">Update your photos</span>
              </div>
            </div>
            <p className="text-afa-lime font-bold text-base mt-2">
              Simply Smarter Email: Just a few simple choices and you're Done!
            </p>
          </div>

          {/* Feedback Section */}
          {!showFeedbackForm ? (
            <div className="p-4 bg-white rounded-xl border border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-5 h-5 text-afa-navy" />
                  <div>
                    <p className="text-sm font-semibold text-afa-navy">Still stuck?</p>
                    <p className="text-xs text-slate-500">Send us feedback and we'll help!</p>
                  </div>
                </div>
                <Button 
                  onClick={() => setShowFeedbackForm(true)}
                  className="bg-afa-navy hover:bg-afa-navy/90 text-white text-sm"
                  size="sm"
                >
                  Send Feedback
                </Button>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-gradient-to-r from-afa-navy-light to-slate-50 rounded-xl border border-slate-200">
              {submitted ? (
                <div className="text-center py-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <p className="font-semibold text-green-800">Thanks for your feedback!</p>
                  <p className="text-sm text-slate-600">We'll look into this soon.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <MessageSquare className="w-5 h-5 text-afa-navy" />
                    <p className="text-sm font-semibold text-slate-800">Send us your feedback</p>
                  </div>
                  
                  {/* Feedback Type */}
                  <div className="flex gap-2 mb-3">
                    <button
                      onClick={() => setFeedbackType('issue')}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        feedbackType === 'issue' 
                          ? 'bg-afa-navy text-white' 
                          : 'bg-white border border-slate-200 text-slate-600 hover:border-afa-navy'
                      }`}
                    >
                      Report Issue
                    </button>
                    <button
                      onClick={() => setFeedbackType('suggestion')}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        feedbackType === 'suggestion' 
                          ? 'bg-afa-navy text-white' 
                          : 'bg-white border border-slate-200 text-slate-600 hover:border-afa-navy'
                      }`}
                    >
                      Suggestion
                    </button>
                    <button
                      onClick={() => setFeedbackType('question')}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        feedbackType === 'question' 
                          ? 'bg-afa-navy text-white' 
                          : 'bg-white border border-slate-200 text-slate-600 hover:border-afa-navy'
                      }`}
                    >
                      Question
                    </button>
                  </div>

                  {/* Message */}
                  <Textarea
                    placeholder={
                      feedbackType === 'issue' 
                        ? "Describe what's not working... (e.g., 'The URL I tried is example.com and it shows...')"
                        : feedbackType === 'suggestion'
                        ? "What would make this better for you?"
                        : "What would you like to know?"
                    }
                    value={feedbackMessage}
                    onChange={(e) => setFeedbackMessage(e.target.value)}
                    className="mb-3 bg-white min-h-[80px]"
                  />

                  {/* Email (optional) */}
                  <Input
                    type="email"
                    placeholder="Your email (optional - for us to follow up)"
                    value={feedbackEmail}
                    onChange={(e) => setFeedbackEmail(e.target.value)}
                    className="mb-3 bg-white"
                  />

                  {/* Submit */}
                  <div className="flex gap-2 justify-end">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowFeedbackForm(false)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleSubmitFeedback}
                      disabled={!feedbackMessage.trim() || submitting}
                      className="bg-afa-navy hover:bg-afa-navy-dark text-white"
                      size="sm"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Feedback
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function GettingStartedPage() {
  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>
          Getting Started
        </h1>
        <p className="text-lg text-slate-600">
          Learn how to create beautiful emails from any webpage
        </p>
      </div>

      {/* Main Checklist */}
      <GettingStartedChecklist />

      {/* Example Section */}
      <Card className="mb-6 border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
            <Sparkles className="w-5 h-5 text-afa-navy" />
            See It In Action
          </CardTitle>
          <CardDescription>
            Here's a real example of how a webpage transforms into a beautiful email
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Before: The Original Page */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide flex items-center gap-2">
              <span className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center text-xs font-bold">1</span>
              The Original Webpage
            </h4>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
              <div className="flex items-center gap-3 mb-3">
                <Globe className="w-5 h-5 text-slate-400" />
                <a 
                  href={EXAMPLE_URL} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-afa-navy hover:underline flex items-center gap-1 break-all"
                >
                  {EXAMPLE_URL}
                  <ExternalLink className="w-3 h-3 flex-shrink-0" />
                </a>
              </div>
              <p className="text-sm text-slate-600">
                This is a tourism page about Sparta & Alleghany County on the Blue Ridge Parkway website. 
                It has great content: descriptive paragraphs about the area, beautiful landscape photos, 
                and clear sections about things to do.
              </p>
              <div className="mt-3 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                <p className="text-xs text-emerald-700">
                  <CheckCircle2 className="w-3 h-3 inline mr-1" />
                  <strong>Why this works:</strong> Public page, multiple paragraphs of text, high-quality images, clear topic
                </p>
              </div>
            </div>
          </div>

          {/* Arrow */}
          <div className="flex justify-center my-4">
            <div className="w-10 h-10 bg-afa-navy rounded-full flex items-center justify-center">
              <ArrowRight className="w-5 h-5 text-white rotate-90" />
            </div>
          </div>

          {/* After: The Generated Email */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide flex items-center gap-2">
              <span className="w-6 h-6 bg-afa-navy text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
              The Generated Email
            </h4>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
              <div className="mb-3">
                <img 
                  src={EXAMPLE_EMAIL_PREVIEW}
                  alt="Generated email example - Alleghany County"
                  className="w-full max-w-md mx-auto rounded-lg shadow-lg border border-slate-200"
                />
              </div>
              <div className="text-center">
                <p className="text-sm text-slate-600 mb-2">
                  The AI automatically created:
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  <span className="px-3 py-1 bg-afa-navy-light text-afa-navy text-xs rounded-full">
                    Catchy headline
                  </span>
                  <span className="px-3 py-1 bg-afa-navy-light text-afa-navy text-xs rounded-full">
                    Engaging summary
                  </span>
                  <span className="px-3 py-1 bg-afa-navy-light text-afa-navy text-xs rounded-full">
                    Organized sections
                  </span>
                  <span className="px-3 py-1 bg-afa-navy-light text-afa-navy text-xs rounded-full">
                    Beautiful images
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Steps */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle style={{fontFamily: 'Manrope'}}>How It Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-afa-navy-light rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-xl font-bold text-afa-navy">1</span>
              </div>
              <h4 className="font-semibold text-slate-800 mb-1">Paste a URL</h4>
              <p className="text-sm text-slate-500">Enter any public webpage with content and images</p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-afa-navy-light rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-xl font-bold text-afa-navy">2</span>
              </div>
              <h4 className="font-semibold text-slate-800 mb-1">AI Does the Work</h4>
              <p className="text-sm text-slate-500">We scrape content, generate copy, and design your email</p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-afa-navy-light rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-xl font-bold text-afa-navy">3</span>
              </div>
              <h4 className="font-semibold text-slate-800 mb-1">Customize & Send</h4>
              <p className="text-sm text-slate-500">Tweak layouts, themes, and content, then send!</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default GettingStartedPage;
